// src/components/CompanySiteResult.ts
//
// Generic renderer for EXTERNAL company / official-site results.
//
// External company data is owned by the backend:
//   backend/search_index/official_sites.json
//
// Do not add a COMPANY_SITES catalog to this file.
// AyeSearch-owned properties remain in AyeOwnedSiteResult.ts.

import "./AyeOwnedSiteResult.css";
import "./OfficialSiteModules.css";
import { renderOfficialSiteModules, type OfficialVideo, type OfficialStoreApp } from "./OfficialSiteModules";

export interface CompanySiteLink {
  label: string;
  description: string;
  url: string;
}

export interface CompanySiteDefinition {
  videos?: OfficialVideo[];
  more_videos_url?: string;
  store_app?: OfficialStoreApp | null;
  slug?: string;
  name?: string;
  title?: string;
  domain: string;
  official_url?: string;
  url?: string;
  display_url?: string;
  description: string;
  favicon_url?: string;
  favicon_source_url?: string;
  favicon_scale?: number;
  favicon_origin?: string;
  crawl_status?: string;
  image_url?: string;
  sitelinks?: CompanySiteLink[];

  // Optional backend-owned allowlist for supporting indexed results shown
  // underneath the dedicated company result. The order is also the display
  // priority for matching result domains.
  result_domains?: string[];
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function renderCompanySiteResult(
  site: CompanySiteDefinition,
): string {
  const hasActionRows = (
    (site.slug === "ayestore" && site.domain === "ayestore.ayesearch.com") ||
    (site.slug === "gaming" && site.domain === "gaming.ayesearch.com") ||
    (site.slug === "calendar" && site.domain === "calendar.ayesearch.com") ||
    (site.slug === "docs" && site.domain === "adocs.ayesearch.com") ||
    (site.slug === "vibejoo" && site.domain === "vibejoo.ayesearch.com")
  );
  const rawTitle = site.name || site.title || site.domain;
  const rawUrl = site.official_url || site.url || `https://${site.domain}/`;
  const rawDisplayUrl =
    site.display_url || rawUrl.replace(/\/$/, "");

  const title = escapeHtml(rawTitle);
  const url = escapeHtml(rawUrl);
  const displayUrl = escapeHtml(rawDisplayUrl);
  const description = escapeHtml(site.description || "");

  const faviconUrl =
    site.favicon_url?.trim() ||
    site.favicon_source_url?.trim() ||
    "";

  const safeFaviconUrl = faviconUrl
    ? escapeHtml(faviconUrl)
    : "";

  const faviconScale =
    typeof site.favicon_scale === "number" &&
    Number.isFinite(site.favicon_scale)
      ? Math.max(0.8, Math.min(site.favicon_scale, 2.3))
      : 1;

  const sitelinks = Array.isArray(site.sitelinks)
    ? site.sitelinks
    : [];

  return `
    <section
      class="aye-owned-site-results company-site-results${hasActionRows ? " official-action-rows" : ""}"
      aria-label="${title} official company result"
    >
      <article class="aye-owned-primary-result company-site-primary-result">
        <a
          class="aye-owned-source-row"
          href="${url}"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="aye-owned-favicon">
            ${
              safeFaviconUrl
                ? `
                  <img
                    src="${safeFaviconUrl}"
                    alt=""
                    loading="eager"
                    style="transform: scale(${faviconScale}); transform-origin: center;"
                    onerror="this.style.display='none'"
                  />
                `
                : `
                  <span class="aye-owned-favicon-letter">
                    ${title.slice(0, 1).toUpperCase()}
                  </span>
                `
            }
          </span>

          <span class="aye-owned-source-copy">
            <strong>${title}</strong>
            <span>${displayUrl}</span>
          </span>
        </a>

        <h2 class="aye-owned-title">
          <a
            href="${url}"
            target="_blank"
            rel="noopener noreferrer"
          >
            ${title}
          </a>
        </h2>

        ${
          description
            ? `
              <p class="aye-owned-description">
                ${description}
              </p>
            `
            : ""
        }

        ${
          sitelinks.length
            ? `
              <nav
                class="aye-owned-sitelinks"
                aria-label="${title} related links"
              >
                ${sitelinks
                  .map(
                    (link) => `
                      <a
                        class="aye-owned-sitelink"
                        href="${escapeHtml(link.url)}"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span class="aye-owned-sitelink-text">
                          <strong>${escapeHtml(link.label)}</strong>
                          <span>${escapeHtml(link.description)}</span>
                        </span>

                        <span
                          class="aye-owned-sitelink-indicator"
                          aria-hidden="true"
                        >
                          ${hasActionRows ? "›" : "↗"}
                        </span>
                      </a>
                    `,
                  )
                  .join("")}
              </nav>
            `
            : ""
        }
      </article>
      ${renderOfficialSiteModules(site)}
    </section>
  `;
}
